'use client';

import CustomButton from '@/components/custom-button';
import CustomInput from '@/components/custom-input';
import CustomPage from '@/components/custom-page';
import CustomToast from '@/components/custom-toast';
import { template_warning } from '@/components/custom-toast/sonner-with-template';
import { pageDirectories } from '@/constant/route';
import signInCredentials from '@/keys/signInCredentials';
import { Link, useRouter } from '@/navigation';
import { LoginInput } from '@/server/api/v1/auth/schema';
import { AuthErrorCode } from '@/server/config/auth.error';
import { LoginMethod, LoginTarget } from '@/types/enum';
import { LockKeyhole, LogIn, UserRound } from 'lucide-react';
import { getSession, signIn } from 'next-auth/react';
import { useTranslations } from 'next-intl';
import * as React from 'react';
import { z } from 'zod';
import { ChangePasswordForm } from '../change-password-first-time-form';
import { Render3rdPartyLogin } from '../sign-in-form/3rd-party-login';
import { RenderAlertLogin } from '../sign-in-form/alert-login';
import { ObjectSignIn } from '../sign-in-form/object-signin';

const { sonner } = CustomToast;

type LoginError = {
    title: string;
    desc?: string;
};

export default function SignIn(props: {
    loginMethod: Record<LoginMethod, boolean>;
    isOGStudent?: boolean;
}) {
    const transDef = useTranslations('System');
    const transPage = useTranslations('SignIn');

    const [loginTarget, setLoginTarget] = React.useState<LoginTarget>(() => {
        if (props.isOGStudent) {
            return LoginTarget.UIS_Student;
        }
        return LoginTarget.Credential;
    });

    const [actNeeded, setActNeeded] = React.useState<{
        type: 'change-pwd';
        token: string;
        expireIn: number;
    }>();
    const [loginInfo, setLoginInfo] = React.useState<{
        username: string;
        password: string;
    }>();

    const [loginError, setLoginError] = React.useState<LoginError>();

    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [totalFail, setTotalFail] = React.useState(0);

    const router = useRouter();

    const handleAfterSignin = React.useCallback(async () => {
        const session = await getSession();
        if (session?.actionNeeded) {
            /**
             * Cần đổi mật khẩu mới trước khi đăng nhập
             */
            setActNeeded(session.actionNeeded);
        } else {
            window?.location?.reload() ?? router.refresh();
        }
    }, [router]);

    const onLoginWithAccount = React.useCallback(
        async (event: React.SyntheticEvent) => {
            event.preventDefault();
            setIsLoading(true);
            const inputs: any[] = event.target as any;
            try {
                setLoginError(undefined);

                const loginBody: z.infer<typeof LoginInput>['credentials'] = {
                    username: inputs[0].value,
                    password: inputs[1].value,
                    target: loginTarget,
                };

                if (!loginBody.username || !loginBody.password) {
                    setIsLoading(false);
                    sonner.warning(
                        template_warning({
                            title: transPage('PlsEnterFullInfomation'),
                        })
                    );
                    return;
                }

                const result = await signIn(signInCredentials.Credentials, {
                    ...loginBody,
                    redirect: false,
                });

                if (!(result?.error?.length && result.error.length > 0)) {
                    setLoginInfo(loginBody);
                    await handleAfterSignin();
                    return;
                }

                setIsLoading(false);

                /**
                 * Có thông tin mã lỗi
                 */
                if (result.code) {
                    let title = '';
                    switch (result.code) {
                        case AuthErrorCode.InvalidCredentials:
                            title = transPage('AccountInfoIncorrect');
                            break;
                        case AuthErrorCode.Forbidden:
                            title = transPage('AccountDoesNotHaveAccess');
                            break;

                        case AuthErrorCode.ErrorCivilServant:
                            title = transPage('ErrorCivilServant');
                            break;
                    }

                    setLoginError({
                        title,
                        desc:
                            totalFail > 3
                                ? ` ${transPage('IfUThinkThisSystemErrorPlsContactAdmin')}`
                                : '',
                    });
                } else {
                    setLoginError({
                        title: transDef('System_AnErrorHasOccurred'),
                        desc: transDef(
                            'System_PleaseTryAgainOrContactTheAdministrator'
                        ),
                    });
                }
            } catch (error) {
                setLoginError({
                    title: transPage('AccountInfoIncorrect'),
                    desc:
                        totalFail > 3
                            ? ` ${transPage('IfUThinkThisSystemErrorPlsContactAdmin')}`
                            : '',
                });
            }

            setTotalFail((x) => x + 1);
        },
        [handleAfterSignin, loginTarget, totalFail, transDef, transPage]
    );

    let _child: React.ReactNode;

    if (actNeeded) {
        if (actNeeded.type === 'change-pwd' && loginInfo) {
            _child = (
                <ChangePasswordForm {...actNeeded} loginInfo={loginInfo} />
            );
        }
    } else {
        _child = (
            <>
                <h2 className='text-center text-3xl font-bold uppercase tracking-wider'>
                    {transPage('SignIn')}
                </h2>
                <form
                    className='mt-8'
                    onSubmit={onLoginWithAccount}
                    id='sign-in-form'
                    autoComplete='off'
                >
                    <div className='space-y-4'>
                        {props.isOGStudent && (
                            <ObjectSignIn
                                loginTarget={loginTarget}
                                setLoginTarget={setLoginTarget}
                                disabled={isLoading}
                            />
                        )}

                        <div>
                            <label
                                htmlFor='username'
                                className='block py-2 text-sm font-medium'
                            >
                                {transPage('UserName')}
                            </label>

                            <CustomInput.Prefix
                                prefix={<UserRound size={16} />}
                                id='username'
                                type='text'
                                autoCapitalize='none'
                                autoComplete='username'
                                autoCorrect='off'
                                disabled={isLoading}
                                placeholder={transPage('UserName')}
                                className='no-ring pl-8 shadow-md'
                            />
                        </div>
                        <div>
                            <div className='flex items-center justify-between'>
                                <label
                                    htmlFor='password'
                                    className='block py-2 text-sm font-medium'
                                >
                                    {transPage('Password')}
                                </label>
                                <div className='text-sm'>
                                    <Link
                                        href={pageDirectories['Forgor']}
                                        className='py-2 font-medium text-primary hover:underline dark:text-white'
                                        tabIndex={-1}
                                    >
                                        {transPage('ForgotPassword')}?
                                    </Link>
                                </div>
                            </div>
                            <CustomInput.Prefix
                                prefix={<LockKeyhole size={16} />}
                                isInputPassword
                                id='password'
                                autoComplete='new-password'
                                autoCorrect='off'
                                disabled={isLoading}
                                placeholder={transPage('Password')}
                                className='no-ring pl-8 shadow-md'
                            />
                        </div>
                    </div>

                    <RenderAlertLogin
                        isShow={!!loginError}
                        title={loginError?.title ?? ''}
                        desc={loginError?.desc}
                    />

                    <CustomButton.Root
                        type='submit'
                        className='mt-3 w-full uppercase tracking-wide shadow-md'
                        isLoading={isLoading}
                        icon={<LogIn size={14} strokeWidth={2.5} />}
                    >
                        {transPage('SignIn')}
                    </CustomButton.Root>

                    <Render3rdPartyLogin
                        isDisabledBtn={isLoading}
                        loginTarget={loginTarget}
                        {...props}
                    />

                    {props.isOGStudent && (
                        <div className='mt-6 text-sm font-medium text-muted-foreground'>
                            <p className='text-center'>
                                {transPage('NotAccount')}?{' '}
                                <Link
                                    className='text-primary hover:underline'
                                    href={pageDirectories['Signup']}
                                >
                                    {transPage('SignUp')}.
                                </Link>
                            </p>
                        </div>
                    )}
                </form>
            </>
        );
    }
    return (
        <div className='relative flex min-h-full min-w-full bg-background text-foreground lg:overflow-y-hidden'>
            <h1 className='sr-only'>{transPage('SignIn')}</h1>

            <CustomPage.Wrapper className='relative z-10 w-full'>
                <div className='flex h-full w-full flex-col items-center justify-center bg-background px-4 pb-24 pt-12 text-foreground sm:px-6 lg:px-8'>
                    <div className='w-full max-w-md space-y-8 rounded-md p-8'>
                        {_child}
                    </div>
                </div>
            </CustomPage.Wrapper>
        </div>
    );
}
